import {
  ChangeDetectionStrategy,
  Component,
  inject,
  Input,
  OnInit,
} from '@angular/core';
import { flatten } from 'lodash';
import { combineLatest, Observable } from 'rxjs';
import { filter, map, switchMap } from 'rxjs/operators';
import { TaskWorkspaceStore } from '../../../../../../../core/h3ml/state/task-workspace-store';
import { FeedbackListService } from '../../../../../../../core/shared/feedback/feedback-list.service';
import { ObjectScopingService } from '../../../../../../../core/shared/misc-services/object-scoping.service';
import { getOrThrowStore } from '../../../../../../../cross-team-shared/misc-utils/assert-store';

@Component({
  selector: 'h3-object-feedback-indicator',
  templateUrl: './object-feedback-indicator.component.html',
  styleUrl: './object-feedback-indicator.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ObjectFeedbackIndicatorComponent implements OnInit {
  @Input({ required: true }) objectIndex!: number;
  hasFeedback$!: Observable<boolean>;
  tooltip$!: Observable<string>;

  private objectScopingService = inject(ObjectScopingService);
  private store = inject(TaskWorkspaceStore);
  private feedbackListService = inject(FeedbackListService);

  ngOnInit() {
    this.hasFeedback$ = this.objectScopingService
      .getNestedObjectIds$(this.objectIndex)
      .pipe(
        switchMap((nestedObjectIds) => {
          const objectIds = [this.objectIndex, ...nestedObjectIds];
          return combineLatest([
            this.hasHighlight$(objectIds),
            this.hasShapeFeedback$(objectIds),
          ]).pipe(
            map(
              ([hasHighlight, hasShapeFeedback]) =>
                hasHighlight || hasShapeFeedback,
            ),
          );
        }),
      );
    this.tooltip$ = this.getTooltip$();
  }

  openFeedbackList() {
    if (this.store.isUsingGenAi()) {
      return;
    }

    this.objectScopingService.scopeObject(
      this.objectIndex,
      'feedback_icon',
      'scope',
    );

    this.feedbackListService.openFeedbackListForObject(
      this.objectIndex,
      'object',
    );
  }

  private getShapeIds$(objectIds: number[]) {
    return combineLatest(
      objectIds.map(
        (objectId) =>
          getOrThrowStore(this.store('objects'), objectId)('childShapeIds').$,
      ),
    ).pipe(map((childShapeIds) => new Set(flatten(childShapeIds))));
  }

  private getTooltip$() {
    return this.objectScopingService.getNestedObjectIds$(this.objectIndex).pipe(
      switchMap((nestedObjectIds) => {
        const objectIds = [this.objectIndex, ...nestedObjectIds];
        return combineLatest([
          this.getHighlightFeedback$(objectIds),
          this.getShapeFeedback$(objectIds),
        ]).pipe(
          filter(
            ([highlight, shapeFeedback]) =>
              !!highlight.length || !!shapeFeedback.length,
          ),
          map(([highlight, shapeFeedback]) => {
            let toolTip = '';
            // assume that highlight and shapeFeedback can't be coexistent
            if (highlight.length > 1 || shapeFeedback.length > 1) {
              toolTip = `${highlight.length + shapeFeedback.length} feedback items`;
            } else {
              toolTip = highlight[0]?.errorTag ?? shapeFeedback[0].errorTag;
            }
            return toolTip;
          }),
        );
      }),
    );
  }

  private getShapeFeedback$(objectIds: number[]) {
    return this.getShapeIds$(objectIds).pipe(
      switchMap((shapeIds) =>
        this.store('shapeFeedback').$.pipe(
          map((shapeFeedback) =>
            shapeFeedback.filter(
              (feedback) => feedback.shapeId && shapeIds.has(feedback.shapeId),
            ),
          ),
        ),
      ),
    );
  }

  private hasShapeFeedback$(objectIds: number[]) {
    return this.getShapeFeedback$(objectIds).pipe(
      map((shapeFeedback) => shapeFeedback.length > 0),
    );
  }

  private getHighlightFeedback$(objectIds: number[]) {
    const objectIdSet = new Set(objectIds);

    return this.store('highlightFeedbacks').$.pipe(
      map((feedbacks) =>
        feedbacks.filter((feedback) => objectIdSet.has(feedback.objectId)),
      ),
    );
  }

  private hasHighlight$(objectIds: number[]) {
    return this.getHighlightFeedback$(objectIds).pipe(
      map((highlightFeedbacks) => highlightFeedbacks.length > 0),
    );
  }
}
